
import {
  collection,
  query,
  getDocs,
  limit,
  startAfter,
  orderBy,
  where,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../Firebase/Config';

// A car is one physical unit at one branch. Availability is driven entirely
// by `status` (see CarStatus) — there is no stock quantity or variant tree.
export type CarStatus = 'In Stock' | 'Reserved' | 'Sold' | 'Delivered' | 'In Transit (Import)';

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discount?: number; // Discount percentage (0-100)
  sku: string; // Chassis / VIN number
  categoryId: string;
  subcategoryId: string;
  brandId?: string | null;
  modelId?: string;
  trimId?: string;
  trimName?: string;
  images: string[];
  isFeatured?: boolean;
  isVisible?: boolean;
  branchId?: string;
  branchName?: string;
  tags?: string[];
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // ── Car specification fields (mirrors admin schema) ─────────────────
  modelYear?: number;
  registrationYear?: number;
  mileageKm?: number;
  fuelType?: string;
  transmission?: string;
  engineCapacityCc?: number;
  driveType?: string;
  seatingCapacity?: number;
  exteriorColor?: string;
  interiorColor?: string;
  condition?: string;
  status?: CarStatus;
  registrationNumber?: string;
  ownersCount?: number;
  isImported?: boolean;
  countryOfOrigin?: string;
  features?: string[];
}

export interface PaginatedProducts {
  products: Product[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export type SortOption = 'newness' | 'price-low' | 'price-high';

// Mirrors admin/src/enums/product.ts CAR_CONDITIONS — every value except
// 'Brand New' counts as "Pre-Owned" for the shop's condition filter.
const PRE_OWNED_CONDITIONS = ['Used - Excellent', 'Used - Good', 'Used - Fair', 'Reconditioned', 'Salvage'];

const PRODUCTS_PER_PAGE = 12;

export type ConditionFilter = 'brand-new' | 'pre-owned';

export interface ProductFilters {
  categoryId?: string;
  subcategoryId?: string;  // ← NEW: filter by product.subcategoryId field
  brandId?: string;
  modelId?: string;  // model, dependent on brandId
  trimId?: string;   // trim, dependent on modelId
  searchQuery?: string;
  tags?: string[];
  sortBy?: SortOption;
  condition?: ConditionFilter;
}

// ── Utility: calculate discounted price ──────────────────────────────────────
export const calculateDiscountedPrice = (price: number, discount?: number): number => {
  if (!discount || discount <= 0 || discount > 100) return price;
  return price - (price * discount) / 100;
};

export const hasDiscount = (discount?: number): boolean => {
  return !!discount && discount > 0 && discount <= 100;
};

// ── A car is unavailable once it's no longer sitting in stock ─────────────────
export const isProductOutOfStock = (product: Product): boolean => {
  return !!product.status && product.status !== 'In Stock';
};

// ── Listing display grouping ───────────────────────────────────────────────
//
// Several brand-new units of the same brand+model+trim+color are functionally
// interchangeable to a browsing customer (unlike pre-owned cars, which always
// differ in mileage/condition/year), so the browse/listing pages collapse
// them into a single card with a count. This is purely a display concern —
// each unit keeps its own Car/Product record (own VIN, branch, status); only
// the rendered list is grouped, and clicking through still opens one specific
// unit's detail page.
export interface ProductListingItem {
  // The unit shown on the card — an in-stock one is preferred when available.
  product: Product;
  count: number;
  unitIds: string[];
}

const isGroupableProduct = (product: Product): boolean =>
  product.condition === 'Brand New' && !!product.brandId && !!product.modelId;

const groupingKey = (product: Product): string =>
  ['group', product.brandId, product.modelId, product.trimId || '', product.exteriorColor || ''].join('::');

export const groupProductsForListing = (products: Product[]): ProductListingItem[] => {
  const groupIndex = new Map<string, ProductListingItem>();
  const result: ProductListingItem[] = [];

  for (const product of products) {
    if (!isGroupableProduct(product)) {
      result.push({ product, count: 1, unitIds: [product.id] });
      continue;
    }

    const key = groupingKey(product);
    const existing = groupIndex.get(key);
    if (existing) {
      existing.count += 1;
      existing.unitIds.push(product.id);
      if (isProductOutOfStock(existing.product) && !isProductOutOfStock(product)) {
        existing.product = product;
      }
    } else {
      const item: ProductListingItem = { product, count: 1, unitIds: [product.id] };
      groupIndex.set(key, item);
      result.push(item);
    }
  }

  return result;
};

export const fetchProducts = async (
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  pageSize: number = PRODUCTS_PER_PAGE
): Promise<PaginatedProducts> => {
  try {
    const productsRef = collection(db, 'products');
    let q;

    if (lastDoc) {
      q = query(
        productsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(pageSize)
      );
    } else {
      q = query(
        productsRef,
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    }

    const snapshot = await getDocs(q);
    const products: Product[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
    const hasMore = snapshot.docs.length === pageSize;

    return {
      products,
      lastDoc: lastVisible,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const fetchProductsByCategory = async (
  categoryId: string,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  pageSize: number = PRODUCTS_PER_PAGE
): Promise<PaginatedProducts> => {
  try {
    const productsRef = collection(db, 'products');
    let q;

    if (categoryId === '*') {
      if (lastDoc) {
        q = query(
          productsRef,
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        );
      } else {
        q = query(
          productsRef,
          orderBy('createdAt', 'desc'),
          limit(pageSize)
        );
      }
    } else {
      if (lastDoc) {
        q = query(
          productsRef,
          where('categoryId', '==', categoryId),
          startAfter(lastDoc),
          limit(pageSize + 10)
        );
      } else {
        q = query(
          productsRef,
          where('categoryId', '==', categoryId),
          limit(pageSize + 10)
        );
      }
    }

    const snapshot = await getDocs(q);
    let products: Product[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    if (categoryId !== '*') {
      products.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });
      products = products.slice(0, pageSize);
    }

    const lastVisible = snapshot.docs[Math.min(snapshot.docs.length - 1, pageSize - 1)] || null;
    const hasMore = snapshot.docs.length > pageSize;

    return {
      products,
      lastDoc: lastVisible,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
};

export const fetchFilteredProducts = async (
  filters: ProductFilters,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  pageSize: number = PRODUCTS_PER_PAGE
): Promise<PaginatedProducts> => {
  try {
    const productsRef = collection(db, 'products');
    const fetchSize = pageSize * 3;
    let q;
    const constraints: any[] = [];

    // ── Subcategory filter — takes priority over categoryId ───────────────────
    // products store their sub's ID in product.subcategoryId
    // (Firestore doc field: subcategoryId = "XYZ", categoryId = parent's ID)
    if (filters.subcategoryId && filters.subcategoryId !== '*' && filters.subcategoryId !== 'undefined') {
      constraints.push(where('subcategoryId', '==', filters.subcategoryId));
    }
    // ── Parent category filter (only when subcategoryId is NOT set) ───────────
    else if (filters.categoryId && filters.categoryId !== '*' && filters.categoryId !== 'undefined') {
      constraints.push(where('categoryId', '==', filters.categoryId));
    }

    // ── Brand filter ──────────────────────────────────────────────────────────
    if (filters.brandId && filters.brandId !== '*' && filters.brandId !== 'undefined') {
      constraints.push(where('brandId', '==', filters.brandId));
    }

    // ── Tag filter (single tag only — Firestore array-contains limit) ─────────
    if (filters.tags && filters.tags.length === 1) {
      constraints.push(where('tags', 'array-contains', filters.tags[0]));
    }

    // ── Condition filter (Brand New vs. Pre-Owned) ────────────────────────────
    if (filters.condition === 'brand-new') {
      constraints.push(where('condition', '==', 'Brand New'));
    } else if (filters.condition === 'pre-owned') {
      constraints.push(where('condition', 'in', PRE_OWNED_CONDITIONS));
    }

    if (constraints.length > 0) {
      if (lastDoc) {
        q = query(
          productsRef,
          ...constraints,
          startAfter(lastDoc),
          limit(fetchSize)
        );
      } else {
        q = query(
          productsRef,
          ...constraints,
          limit(fetchSize)
        );
      }
    } else {
      if (lastDoc) {
        q = query(
          productsRef,
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(fetchSize)
        );
      } else {
        q = query(
          productsRef,
          orderBy('createdAt', 'desc'),
          limit(fetchSize)
        );
      }
    }

    const snapshot = await getDocs(q);

    let products: Product[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    // ── Visibility filter (client-side — legacy docs have no isVisible field
    //    at all, so only an explicit `false` hides a car; missing = visible) ──
    products = products.filter(product => product.isVisible !== false);

    // ── Search filter (client-side) ───────────────────────────────────────────
    if (filters.searchQuery?.trim()) {
      const searchLower = filters.searchQuery.toLowerCase();
      products = products.filter(product =>
        product.name.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower) ||
        product.sku?.toLowerCase().includes(searchLower)
      );
    }

    // ── Multi-tag filter (client-side, >1 tag) ────────────────────────────────
    if (filters.tags && filters.tags.length > 1) {
      products = products.filter(product =>
        product.tags &&
        Array.isArray(product.tags) &&
        filters.tags!.some(tag => product.tags!.includes(tag))
      );
    }

    // ── Model / Trim filter (client-side — avoids extra composite indexes
    //    on top of the existing category/brand/tags/condition combinations) ──
    if (filters.modelId && filters.modelId !== '*') {
      products = products.filter(product => product.modelId === filters.modelId);
    }
    if (filters.trimId && filters.trimId !== '*') {
      products = products.filter(product => product.trimId === filters.trimId);
    }

    // ── Sort ──────────────────────────────────────────────────────────────────
    switch (filters.sortBy) {
      case 'newness':
        products.sort((a, b) => {
          const aTime = a.createdAt?.toMillis() || 0;
          const bTime = b.createdAt?.toMillis() || 0;
          return bTime - aTime;
        });
        break;
      case 'price-low':
        products.sort((a, b) => {
          const aInfo = getProductDisplayInfo(a);
          const bInfo = getProductDisplayInfo(b);
          return aInfo.discountedPrice - bInfo.discountedPrice;
        });
        break;
      case 'price-high':
        products.sort((a, b) => {
          const aInfo = getProductDisplayInfo(a);
          const bInfo = getProductDisplayInfo(b);
          return bInfo.discountedPrice - aInfo.discountedPrice;
        });
        break;
    }

    const paginatedProducts = products.slice(0, pageSize);

    const lastVisible = snapshot.docs[Math.min(snapshot.docs.length - 1, pageSize - 1)] || null;
    const hasMore = products.length > pageSize;

    return {
      products: paginatedProducts,
      lastDoc: lastVisible,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching filtered products:', error);
    throw error;
  }
};

export const fetchAvailableTags = async (): Promise<string[]> => {
  try {
    const productsRef = collection(db, 'products');
    const q = query(productsRef, limit(1000));
    const snapshot = await getDocs(q);

    const tagsSet = new Set<string>();
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.tags && Array.isArray(data.tags)) {
        data.tags.forEach((tag: string) => tagsSet.add(tag));
      }
    });

    return Array.from(tagsSet).sort();
  } catch (error) {
    console.error('Error fetching tags:', error);
    return [];
  }
};

export const fetchProductsBySubcategory = async (
  subcategoryId: string,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  pageSize: number = PRODUCTS_PER_PAGE
): Promise<PaginatedProducts> => {
  try {
    const productsRef = collection(db, 'products');
    let q;

    if (lastDoc) {
      q = query(
        productsRef,
        where('subcategoryId', '==', subcategoryId),
        startAfter(lastDoc),
        limit(pageSize + 10)
      );
    } else {
      q = query(
        productsRef,
        where('subcategoryId', '==', subcategoryId),
        limit(pageSize + 10)
      );
    }

    const snapshot = await getDocs(q);
    let products: Product[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    products.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    products = products.slice(0, pageSize);

    const lastVisible = snapshot.docs[Math.min(snapshot.docs.length - 1, pageSize - 1)] || null;
    const hasMore = snapshot.docs.length > pageSize;

    return {
      products,
      lastDoc: lastVisible,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching products by subcategory:', error);
    throw error;
  }
};

// ── Core display info ─────────────────────────────────────────────────────────
export const getProductDisplayInfo = (product: Product) => {
  const price = product.price;
  const discount = product.discount;
  const discountedPrice = calculateDiscountedPrice(price, discount);
  return {
    price,
    discount,
    discountedPrice,
    hasDiscount: hasDiscount(discount),
    isOutOfStock: isProductOutOfStock(product),
    sku: product.sku
  };
};

export const fetchFeaturedProducts = async (maxProducts: number = 10): Promise<Product[]> => {
  try {
    const productsRef = collection(db, 'products');
    const q = query(
      productsRef,
      where('isFeatured', '==', true),
      orderBy('createdAt', 'desc'),
      limit(maxProducts)
    );

    const snapshot = await getDocs(q);
    const products: Product[] = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as Product))
      .filter(product => product.isVisible !== false);

    return products;
  } catch (error) {
    console.error('Error fetching featured products:', error);
    throw error;
  }
};

export const fetchProductById = async (productId: string): Promise<Product | null> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);

    if (productSnap.exists()) {
      const product = { id: productSnap.id, ...productSnap.data() } as Product;
      // A car hidden from the client site shouldn't be reachable by direct
      // link to its detail page either.
      if (product.isVisible === false) return null;
      return product;
    } else {
      console.log('No such product!');
      return null;
    }
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};
